<template>
  <v-card class="page-card">
    <agrochemical-application-form v-if="canUpdate"></agrochemical-application-form>
    <v-toolbar color="primary" class="page-toolbar" elevation="0">
      <v-toolbar-title>Aplicaciones</v-toolbar-title>
      <v-divider class="mx-4" inset vertical></v-divider>
      <v-text-field
        v-model="search"
        append-inner-icon="search"
        label="Buscar..."
        single-line
        hide-details
        variant="solo-filled"
        density="comfortable"
      />
    </v-toolbar>

    <div class="table-wrapper">
      <v-data-table
        :headers="headers"
        :items="agroApplications"
        :search="search"
        :items-per-page-options="CONST.ROWS_PER_PAGE_ITEMS"
        :items-per-page-text="CONST.ROWS_PER_PAGE_TEXT">
        <template #[`item.applicationType.name`]="{ item }">
          {{ getRawItem(item).applicationType?.name || '' }}
        </template>
        <template #[`item.landBatch.name`]="{ item }">
          {{ getRawItem(item).landBatch?.name || '' }}
        </template>
        <template #[`item.applicationDate`]="{ item }">
          {{ formatLocalDate(getRawItem(item).applicationDate) }}
        </template>
        <template #[`item.actions`]="{ item }">
          <div class="action-cell action-cell-wide">
            <v-btn v-if="canUpdate" icon size="small" variant="text" class="table-action-button" aria-label="Editar" @click="editItem(getRawItem(item))">
              <v-icon>edit</v-icon>
            </v-btn>
            <v-btn v-if="canDelete" icon size="small" variant="text" class="table-action-button table-action-danger" aria-label="Eliminar" @click="deleteItem(getRawItem(item))">
              <v-icon>delete</v-icon>
            </v-btn>
            <v-btn icon size="small" variant="text" class="table-action-button" aria-label="Ver dosis" @click="openDetailDialog(getRawItem(item))">
              <v-icon>list_alt</v-icon>
            </v-btn>
          </div>
        </template>
        <template #no-data>
          <span>{{ CONST.NO_RESULTS_CHECK_FILTERS }}</span>
          <v-btn color="primary" class="ml-3" @click="initialize">{{ CONST.REFRESH }}</v-btn>
        </template>
      </v-data-table>
    </div>

    <v-dialog v-model="detailDialog" max-width="820px" scrollable>
      <v-card class="dialog-card">
        <v-card-title class="dialog-title">
          Dosis por agroquimico
          <v-spacer></v-spacer>
          <v-btn icon variant="text" aria-label="Cerrar" @click="closeDetailDialog">
            <v-icon>close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text>
          <v-form v-if="canUpdate" ref="addDetailForm" v-model="validAddDetail" validate-on="lazy">
            <v-row dense>
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="agrochemicalItem.dosage"
                  label="Dosis"
                  type="number"
                  step="0.1"
                  min="0"
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.requiredFieldRule, rules.min0]"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-autocomplete
                  v-model="agrochemicalItem.agrochemical"
                  label="Agroquimico"
                  no-data-text="No se encuentran agroquimicos."
                  item-title="name"
                  :items="getAgrochemicals"
                  return-object
                  variant="outlined"
                  density="comfortable"
                  :rules="[rules.requiredFieldRule]"
                />
              </v-col>
              <v-col cols="12" md="2" class="d-flex align-center">
                <v-btn color="primary" block class="text-white" @click="addAgrochemicalAppItem">Agregar</v-btn>
              </v-col>
            </v-row>
          </v-form>

          <v-divider class="my-4"></v-divider>

          <v-alert v-if="applicationDetails.length === 0" type="info" variant="tonal">
            No hay dosis cargadas para esta aplicacion.
          </v-alert>
          <div v-else class="table-wrapper">
            <v-data-table
              :headers="detailHeaders"
              :items="applicationDetails"
              :items-per-page="-1"
              hide-default-footer>
              <template #[`item.agrochemical.name`]="{ item }">
                {{ getRawItem(item).agrochemical?.name || '' }}
              </template>
              <template #[`item.actions`]="{ item }">
                <div class="action-cell">
                  <v-btn v-if="canUpdate" icon size="small" variant="text" class="table-action-button table-action-danger" aria-label="Eliminar dosis" @click="deleteAgrochemicalAppItem(getRawItem(item))">
                    <v-icon>delete</v-icon>
                  </v-btn>
                </div>
              </template>
            </v-data-table>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import CONST from '../../services/common/Constants.js'
import { mapGetters } from 'vuex'
import EditApplicationForm from './EditApplicationForm'
import { formatLocalDate as formatDate, toLocalDateInput } from '../../services/common/UtilsFunctions'

export default {
  name: 'ApplicationAgrochemicalTable',
  components: {
    'agrochemical-application-form': EditApplicationForm
  },
  data: () => ({
    CONST: {},
    search: '',
    rules: {
      requiredFieldRule: v => !!v || 'Campo requerido.',
      min0: v => (v > 0) || 'Debe ingresar un valor mayor que 0'
    },
    headers: [
      {title: 'Aplicacion Nro.', align: 'center', key: 'applicationNumber'},
      {title: 'Tipo', align: 'center', key: 'applicationType.name'},
      {title: 'Lote', align: 'center', key: 'landBatch.name'},
      {title: 'Cantidad Hectareas', align: 'center', key: 'haAmount', sortable: false},
      {title: 'Fecha de Aplicacion', align: 'center', key: 'applicationDate'},
      {title: 'Acciones', align: 'center', key: 'actions', sortable: false}
    ],
    detailHeaders: [
      {title: 'Agroquimico', align: 'center', key: 'agrochemical.name'},
      {title: 'Dosis', align: 'center', key: 'dosage'},
      {title: 'Acciones', align: 'center', key: 'actions', sortable: false}
    ],
    detailDialog: false,
    validAddDetail: true,
    selectedApplication: null,
    agroApplications: [],
    applicationDetails: [],
    agrochemicalItem: {agrochemical: null, dosage: null, application: null}
  }),

  computed: {
    ...mapGetters(['isRefreshTableRequired']),
    ...mapGetters('agrochemicalModule', ['getAgrochemicals', 'isEditApplicationDialogActived']),
    ...mapGetters('authModule', ['hasPermission']),
    canUpdate () { return this.hasPermission('agrochemicals.applications.update') },
    canDelete () { return this.hasPermission('agrochemicals.applications.delete') }
  },

  created () {
    this.CONST = CONST
    this.initialize()
  },

  watch: {
    'isRefreshTableRequired': function (newValue, oldValue) {
      if (newValue === true && oldValue === false) {
        this.initialize()
      }
    },
    'isEditApplicationDialogActived': function (newValue, oldValue) {
      if (newValue === false && oldValue === true) {
        this.initialize()
      }
    }
  },

  methods: {
    initialize () {
      this.$store.dispatch('agrochemicalModule/getAgroApplications').then(() => {
        this.agroApplications = this.$store.getters['agrochemicalModule/getAgroApplications']
      })
    },

    getRawItem (item) {
      return item.raw || item
    },

    formatLocalDate (value) {
      return formatDate(value)
    },

    deleteItem (item) {
      confirm(`Estas seguro de querer eliminar esta aplicacion: ${item.applicationNumber}?`) && this.$store.dispatch('agrochemicalModule/deleteAgroApplication', item.id).then(() => {
        this.initialize()
      }).catch(error => console.log('There was an error trying to delete Agrochemical Application.' + error))
    },

    editItem (item) {
      item.applicationDate = toLocalDateInput(item.applicationDate)
      this.$store.dispatch('agrochemicalModule/setSelectedApplicationItem', item)
      this.$store.dispatch('agrochemicalModule/setEditApplicationDialogActived', true)
    },

    async openDetailDialog (item) {
      this.selectedApplication = item
      await this.loadApplicationDetails(item.id)
      this.detailDialog = true
    },

    async loadApplicationDetails (applicationId) {
      await this.$store.dispatch('agrochemicalModule/getApplicationDetails', applicationId)
      this.applicationDetails = this.$store.getters['agrochemicalModule/getApplicationDetails']
    },

    async addAgrochemicalAppItem () {
      const result = await this.$refs.addDetailForm.validate()
      const isValid = result === true || result.valid
      if (!isValid || !this.selectedApplication) {
        return
      }
      this.agrochemicalItem.application = this.selectedApplication
      try {
        await this.$store.dispatch('agrochemicalModule/saveAgroApplicationDetail', this.agrochemicalItem)
        await this.loadApplicationDetails(this.selectedApplication.id)
        this.resetDetailForm()
      } catch (e) {
        console.log('Error saving agrochemical application detail.', e)
      }
    },

    deleteAgrochemicalAppItem (item) {
      if (!this.selectedApplication) {
        return
      }
      confirm(`Estas seguro de querer eliminar este item aplicado: ${item.agrochemical.name}?`) && this.$store.dispatch('agrochemicalModule/deleteAgroApplicationDetail', item.id).then(() => {
        this.loadApplicationDetails(this.selectedApplication.id)
      }).catch(error => console.log('There was an error trying to delete Agrochemical Application Item Detail.' + error))
    },

    resetDetailForm () {
      this.$refs.addDetailForm.resetValidation()
      this.validAddDetail = true
      this.agrochemicalItem = {agrochemical: null, dosage: null, application: null}
    },

    closeDetailDialog () {
      this.detailDialog = false
      this.selectedApplication = null
      this.applicationDetails = []
      this.resetDetailForm()
    }
  }
}
</script>
